import { EndPoints, httpService } from "@/Core/Client";
import { useState } from "react";

import { Config } from "@/components/crud/crud-config";
import CrudPage from "@/components/crud/page";
import { DynamicBadge } from "@/components/dynamic-badge";
import TableActions from "@/components/table-actions";
import { getPaymentStatusColors } from "@/components/table-utils/get-status-color";
import useQueryKeys from "@/config/query-keys";
import {
    PaymentFormDto,
    PaymentResponseDto,
} from "@/Dtos/payment-dto";
import DateCell from "@/ui/date-cell";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "rizzui";
import { useModal } from "../shared/modal-views/use-modal";
import { Navigate, useParams } from "react-router-dom";

const config: Config<PaymentResponseDto, PaymentFormDto> = {
    endPoint: "", // Will be set in the MainPage component
    queryKey: useQueryKeys.payments,
    columns: [],
    formOptions: {
        customSize: "1200px",
        sendAsFormData: true,
        formClassName:
            "grid grid-cols-1 gap-5 @container md:grid-cols-2 lg:grid-cols-3 [&_label]:font-medium",
    },
    fields: [],
    titles: {
        pageTitle: "Payments",
    },
    filterProps: {
        useDrawer: true,
        sortingClassName: "w-auto",
        directionClassName: "w-auto",
    },
};

const columnHelper = createColumnHelper<PaymentResponseDto>();

const Columns = [
    columnHelper.display({
        id: "select",
        size: 50,
        header: ({ table }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select all Rows"
                checked={table.getIsAllPageRowsSelected()}
                onChange={() => table.toggleAllPageRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select Row"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    }),

    columnHelper.accessor("reference", {
        id: "reference",
        size: 200,
        header: "Reference",
        enableSorting: false,
        cell: ({ row }) => row.original.reference,
    }),
    columnHelper.accessor("amount", {
        id: "amount",
        size: 150,
        header: "Amount",
        cell: ({ row }) => (row.original.currency === "USD" ? "$ " : "SLSH ") + row.original.amount.toCurrency(),
    }),
    columnHelper.accessor("source", {
        id: "source",
        size: 140,
        header: "Source",
        cell: ({ row }) => row.original.source,
    }),
    columnHelper.accessor("paymentMethod", {
        id: "paymentMethod",
        size: 150,
        header: "Account",
        cell: ({ row }) => row.original.paymentMethod.account,
    }),
    columnHelper.accessor("status", {
        id: "status",
        size: 140,
        header: "Status",
        cell: ({ row: { original } }) => (
            <DynamicBadge
                status={original.status}
                classNameBadge={getPaymentStatusColors(original.status)[1]}
                classNameText={getPaymentStatusColors(original.status)[0]}
            />
        ),
    }),
    columnHelper.accessor("paidAt", {
        id: "paidAt",
        size: 160,
        header: "Date",
        cell: ({ row }) => (
            <DateCell
                date={new Date(row.original.paidAt ?? "")}
            />
        ),
    }),
    columnHelper.display({
        id: "actions",
        size: 80,
        cell: ({ row }) => {
            const { openModal } = useModal();
            const [isLoading, setLoading] = useState(false);
            const queryClient = useQueryClient();

            return (
                <TableActions
                    row={row.original}
                    onDelete={() => {
                        setLoading(true);
                        httpService(config.endPoint).delete(row.original.id!);
                        queryClient.refetchQueries({
                            queryKey: config.queryKey,
                        });
                        setLoading(false);
                    }}
                    actions={[]}
                />
            );
        },
    }),
];

config.columns = Columns;

export default function MainPage() {
    const params = useParams();
    if (!params || !params.id) {
        return <Navigate to="/venues" />;
    }

    config.endPoint = EndPoints.venues.payments(params.id);

    return CrudPage<PaymentResponseDto, PaymentFormDto>({ config: config });
}

